import { AxiosError } from 'axios';
import Badge from 'components/base/Badge';
import ReactSelect from 'components/base/ReactSelect';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import CreateProductFromUnencodedModal from 'components/modals/CreateProductFromUnencodedModal';
import ProductMovementBadge from 'components/shared/common/ProductMovementBadge';
import ProductFitmentPreview from 'components/shared/common/ProductFitmentPreview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import useDebounce from 'hooks/useDebounce';
import { useAuth } from 'hooks/useAuth';
import { useBranchContext } from 'providers/BranchProvider';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  MutableRefObject
} from 'react';
import { Button, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatLongIsoDate, formatLongIsoDateOrDash } from 'utils/formatLongIsoDate';
import { toastConfig } from 'utils/toastConfig';
import {
  TResupplyItem,
  TResupplyPotentialProduct,
  TResupplyPotentialProductsResult,
  TResupplySupplier,
  TSupplierDetails
} from 'utils/types';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';
import DataTable from '../BranchProductInventory/DataTable';
import SupplierName from '../PurchaseOrder/SupplierName';
import useFetchSupplier from '../SupplierPayment/useFetchSupplier';
import AddResupplyItemForm, {
  AddResupplyItemFormHandle
} from './AddResupplyItemForm';
import EditResupplyItemForm, {
  EditResupplyItemFormHandle
} from './EditResupplyItemForm';

type SelectOption = {
  label: string;
  value: number;
};

const RESUPPLY_SOURCE_OPTIONS = [
  { value: 'Requested', label: 'Requested' },
  { value: 'CustomerRequested', label: 'Customer requested' },
  { value: 'BelowThreshold', label: 'Below threshold' },
  { value: 'MissingQuantity', label: 'Missing qty' },
  { value: 'Unfulfilled', label: 'Unfulfilled' },
  { value: 'NoStock', label: 'No stock' }
] as const;

type ResupplyPage = {
  data?: TResupplyItem[];
  currentPage?: number;
  pageCount?: number;
  pageSize?: number;
  rowCount?: number;
};

type ResupplyInfiniteData = {
  pages: ResupplyPage[];
  pageParams: unknown[];
};

const isResupplyInfiniteData = (value: unknown): value is ResupplyInfiniteData =>
  Boolean(
    value &&
      typeof value === 'object' &&
      Array.isArray((value as ResupplyInfiniteData).pages) &&
      Array.isArray((value as ResupplyInfiniteData).pageParams)
  );

const removeResupplyItemsFromCache = (
  data: unknown,
  ids: ReadonlySet<number>
): unknown => {
  if (!isResupplyInfiniteData(data)) return data;

  let removed = 0;
  const pages = data.pages.map(page => {
    const rows = page.data ?? [];
    const nextRows = rows.filter(row => {
      if (!ids.has(row.resupplyItemId)) return true;
      removed += 1;
      return false;
    });

    if (nextRows.length === rows.length) return page;

    return {
      ...page,
      data: nextRows,
      rowCount: Math.max(0, (page.rowCount ?? rows.length) - (rows.length - nextRows.length))
    };
  });

  return removed === 0 ? data : { ...data, pages };
};

type PotentialLookup = Map<
  number,
  {
    potentialProducts: TResupplyPotentialProduct[];
    suppliers: TResupplySupplier[];
  }
>;

const getPrimarySupplier = (item: TResupplyItem) =>
  item.sourceSupplier ?? item.suppliers?.[0] ?? null;

const getOtherSuppliers = (item: TResupplyItem) => {
  const primaryId = getPrimarySupplier(item)?.supplierId;
  return (item.suppliers ?? []).filter(
    supplier => supplier.supplierId !== primaryId
  );
};

const resolvePotentialProducts = (
  item: TResupplyItem,
  lookup: PotentialLookup
): TResupplyPotentialProduct[] => {
  // The list GET serializes potentialProducts as [] since matching moved to
  // the lazy endpoint, so an empty array must fall through to the lookup.
  const rowPotentials = item.potentialProducts ?? [];
  if (rowPotentials.length > 0) return rowPotentials;
  return lookup.get(item.resupplyItemId)?.potentialProducts ?? [];
};

const resolveSuppliers = (
  item: TResupplyItem,
  lookup: PotentialLookup
): TResupplySupplier[] => {
  const rowSuppliers = item.suppliers ?? [];
  const potentialSuppliers =
    lookup.get(item.resupplyItemId)?.suppliers ?? [];
  if (potentialSuppliers.length === 0) return rowSuppliers;
  const existingIds = new Set(rowSuppliers.map(supplier => supplier.supplierId));
  return [
    ...rowSuppliers,
    ...potentialSuppliers.filter(
      supplier => !existingIds.has(supplier.supplierId)
    )
  ];
};

const withEnrichment = (
  item: TResupplyItem,
  lookup: PotentialLookup
): TResupplyItem => ({
  ...item,
  potentialProducts: resolvePotentialProducts(item, lookup),
  suppliers: resolveSuppliers(item, lookup)
});

type SelectionStore = {
  subscribe: (listener: () => void) => () => void;
  getSize: () => number;
  has: (id: number) => boolean;
  toggle: (id: number, checked: boolean) => void;
  clear: () => void;
  remove: (ids: number[]) => void;
  values: () => number[];
};

const createSelectionStore = (): SelectionStore => {
  let selected = new Set<number>();
  const listeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach(listener => listener());
  };

  return {
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSize: () => selected.size,
    has: id => selected.has(id),
    toggle: (id, checked) => {
      const already = selected.has(id);
      if (checked === already) return;
      const next = new Set(selected);
      if (checked) next.add(id);
      else next.delete(id);
      selected = next;
      notify();
    },
    clear: () => {
      if (selected.size === 0) return;
      selected = new Set();
      notify();
    },
    remove: ids => {
      const next = new Set(selected);
      let changed = false;
      ids.forEach(id => {
        if (next.delete(id)) changed = true;
      });
      if (!changed) return;
      selected = next;
      notify();
    },
    values: () => Array.from(selected)
  };
};

const SelectCell = ({
  store,
  resupplyItemId,
  ariaLabel
}: {
  store: SelectionStore;
  resupplyItemId: number;
  ariaLabel: string;
}) => {
  const checked = useSyncExternalStore(
    store.subscribe,
    () => store.has(resupplyItemId)
  );
  return (
    <Form.Check
      type="checkbox"
      checked={checked}
      onChange={e => store.toggle(resupplyItemId, e.target.checked)}
      onClick={e => e.stopPropagation()}
      aria-label={ariaLabel}
    />
  );
};

const ResupplySelectionActions = ({
  store,
  onDelete,
  onCreate
}: {
  store: SelectionStore;
  onDelete: () => void;
  onCreate: () => void;
}) => {
  const selectedCount = useSyncExternalStore(
    store.subscribe,
    () => store.getSize()
  );
  return (
    <>
      <Button
        variant="outline-danger"
        className="w-10 mb-3"
        disabled={selectedCount === 0}
        onClick={onDelete}
      >
        Delete
        {selectedCount > 0 ? ` (${selectedCount})` : ''}
      </Button>
      <Button
        variant="primary"
        className="w-10 mb-3"
        disabled={selectedCount === 0}
        onClick={onCreate}
      >
        Create PO
        {selectedCount > 0 ? ` (${selectedCount})` : ''}
      </Button>
    </>
  );
};

const CreateResupplyPurchaseOrderForm = ({
  selectedCount,
  supplierIdRef
}: {
  selectedCount: number;
  supplierIdRef: MutableRefObject<number | null>;
}) => {
  const [suppliers, setSuppliers] = useState<TSupplierDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);

  const fetchSupplier = async (search: string = '') => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/suppliers?page=1&pageSize=10&search=${search}`
      );
      setSuppliers(res.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedInput) {
      void fetchSupplier(debouncedInput);
    } else {
      void fetchSupplier();
    }
  }, [debouncedInput]);

  return (
    <Form className="w-100">
      <p className="text-body-secondary fs-9">
        Create a purchase order with {selectedCount} selected product
        {selectedCount === 1 ? '' : 's'}. If this supplier already has an
        open purchase order, the products will be added to it.
      </p>
      <Form.Group>
        <Form.Label>Select Supplier</Form.Label>
        <ReactSelect
          placeholder="Select supplier"
          options={suppliers?.map(supplier => ({
            value: supplier.supplierId,
            label: supplier.name
          }))}
          onChange={newvalue => {
            supplierIdRef.current = (newvalue as SelectOption).value;
          }}
          closeMenuOnSelect
          isLoading={isLoading}
          isSearchable
          onInputChange={newValue => setInput(newValue)}
        />
      </Form.Group>
    </Form>
  );
};

const Resupply = () => {
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectionStore = useRef(createSelectionStore()).current;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const supplierIdRef = useRef<number | null>(null);
  const addItemFormRef = useRef<AddResupplyItemFormHandle>(null);
  const editItemFormRef = useRef<EditResupplyItemFormHandle>(null);
  const { suppliers } = useFetchSupplier();
  const [filters, setFilters] = useState<{
    supplierId?: string;
    source?: string;
  }>({});
  const [createFromUnencodedItem, setCreateFromUnencodedItem] =
    useState<TResupplyItem | null>(null);
  const [unencodedIds, setUnencodedIds] = useState<number[]>([]);

  const handleRowsChange = useCallback((rows: TResupplyItem[]) => {
    const ids = rows
      .filter(row => row.productId == null)
      .map(row => row.resupplyItemId)
      .sort((a, b) => a - b);
    setUnencodedIds(prev => {
      if (
        prev.length === ids.length &&
        prev.every((id, index) => id === ids[index])
      ) {
        return prev;
      }
      return ids;
    });
  }, []);

  const [potentialLookup, setPotentialLookup] = useState<PotentialLookup>(
    () => new Map()
  );

  useEffect(() => {
    setPotentialLookup(prev => (prev.size === 0 ? prev : new Map()));
  }, [selectedBranch?.branchDomain]);

  const missingIds = unencodedIds.filter(id => !potentialLookup.has(id));
  const missingIdsKey = missingIds.join(',');

  const { data: potentialProductsData } = useQuery({
    queryKey: [
      'resupply-potential',
      selectedBranch?.branchDomain,
      missingIdsKey
    ],
    queryFn: async () => {
      const ids = missingIds;
      const { data } = await axiosInstance.post<
        TResupplyPotentialProductsResult[]
      >(`/api/v1/${selectedBranch!.branchDomain}/resupply/potential-products`, {
        resupplyItemIds: ids
      });
      return { ids, results: data };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled:
      missingIds.length > 0 && Boolean(selectedBranch?.branchDomain)
  });

  useEffect(() => {
    if (!potentialProductsData) return;
    setPotentialLookup(prev => {
      const next = new Map(prev);
      const byId = new Map(
        potentialProductsData.results.map(result => [
          result.resupplyItemId,
          result
        ])
      );
      let changed = false;
      for (const id of potentialProductsData.ids) {
        if (next.has(id)) continue;
        const result = byId.get(id);
        next.set(id, {
          potentialProducts: result?.potentialProducts ?? [],
          suppliers: result?.suppliers ?? []
        });
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [potentialProductsData]);

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (!selectedBranch?.branchDomain) {
      throw new Error('Branch domain not set');
    }
    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch.branchDomain}/resupply`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc,
          ...(filters.supplierId ? { supplierId: filters.supplierId } : {}),
          ...(filters.source ? { source: filters.source } : {})
        }
      }
    );
    return data;
  };

  const handleFilterChange = (key: 'supplierId' | 'source', value: string) => {
    setFilters(prev => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    selectionStore.clear();
  };

  const refreshList = () => setListVersion(version => version + 1);

  const bindCreatedProductToResupplyItem = async (productId: number) => {
    const item = createFromUnencodedItem;
    if (!item || !selectedBranch?.branchDomain) {
      throw new Error('Missing resupply item');
    }

    await axiosInstance.put(
      `/api/v1/${selectedBranch.branchDomain}/resupply/${item.resupplyItemId}/product`,
      { productId }
    );
    refreshList();
  };

  const getProductLabel = (item: TResupplyItem) =>
    item.productName ||
    (item.productId ? `product ${item.productId}` : 'Unnamed product');

  const handleDeleteItems = async (ids: number[]) => {
    if (!selectedBranch?.branchDomain || ids.length === 0) return;

    try {
      if (ids.length === 1) {
        await axiosInstance.delete(
          `/api/v1/${selectedBranch.branchDomain}/resupply/${ids[0]}`
        );
      } else {
        await axiosInstance.post(
          `/api/v1/${selectedBranch.branchDomain}/resupply/delete`,
          { resupplyItemIds: ids }
        );
      }
      toast.success(
        ids.length === 1
          ? 'Removed from resupply'
          : `Removed ${ids.length} products from resupply`,
        { ...toastConfig, autoClose: 2000 }
      );
      modalRef.current.hide();
      selectionStore.remove(ids);
      queryClient.setQueriesData(
        { queryKey: ['resupply', selectedBranch.branchDomain] },
        old => removeResupplyItemsFromCache(old, new Set(ids))
      );
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to remove from resupply',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    }
  };

  const openDeleteConfirm = (ids: number[], productLabel?: string) => {
    if (ids.length === 0) {
      toast.error('Select at least one product', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    const message =
      ids.length === 1
        ? `Remove ${productLabel ?? 'this product'} from the resupply list?`
        : `Remove ${ids.length} selected products from the resupply list?`;

    setModalProps({
      title: ids.length === 1 ? 'Remove from resupply' : 'Remove selected',
      content: <p className="mb-0">{message}</p>,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => {
        void handleDeleteItems(ids);
      },
      onSubmitCustomDisplay: 'Remove'
    });
    modalRef.current.show();
  };

  const openEditItem = (item: TResupplyItem) => {
    setModalProps({
      title: 'Edit Item',
      content: (
        <EditResupplyItemForm
          key={item.resupplyItemId}
          ref={editItemFormRef}
          resupplyItemId={item.resupplyItemId}
          productId={item.productId ?? null}
          productName={item.productName ?? ''}
          quantity={item.quantity}
          unitOfMeasure={item.unitOfMeasure}
          comment={item.comment ?? ''}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: async () => {
        const result = await editItemFormRef.current?.submit();
        if (result) {
          modalRef.current.hide();
          setPotentialLookup(prev => {
            if (!prev.has(item.resupplyItemId)) return prev;
            const next = new Map(prev);
            next.delete(item.resupplyItemId);
            return next;
          });
          refreshList();
        }
      },
      onSubmitCustomDisplay: 'Save'
    });
    modalRef.current.show();
  };

  const columns: ColumnDef<TResupplyItem>[] = useMemo(
    () => [
      {
        id: 'select',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <SelectCell
            store={selectionStore}
            resupplyItemId={original.resupplyItemId}
            ariaLabel={`Select ${original.productName ?? original.productId}`}
          />
        )
      },
      {
        id: 'productName',
        accessorKey: 'productName',
        header: 'Product',
        enableSorting: true,
        cell: ({ row: { original } }) => {
          const item = withEnrichment(original, potentialLookup);
          const name =
            item.productName ||
            (item.productId
              ? `product ${item.productId}`
              : 'Unnamed product');
          const similarNames = (item.potentialProducts ?? [])
            .map(product => product.name)
            .join(', ');
          return (
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {original.productId ? (
                  <Link
                    to={`/product/${encodeURIComponent(
                      selectedBranch?.branchDomain as string
                    )}/${original.productId}`}
                    className="text-decoration-none fw-bold"
                  >
                    {name}
                  </Link>
                ) : (
                  <span className="fw-bold">{name}</span>
                )}
                {original.isMissingQuantity && (
                  <Badge bg="info" className="fs-10">
                    Missing qty from previous order
                  </Badge>
                )}
                {original.source === 'NoStock' && (
                  <Badge bg="danger" className="fs-10">
                    No stock at supplier
                  </Badge>
                )}
                {original.source === 'BelowThreshold' &&
                  !original.wasOrderedBefore && (
                    <Badge bg="danger" className="fs-10">
                      Below threshold
                    </Badge>
                  )}
                {original.source === 'Requested' && (
                  <Badge bg="primary" className="fs-10">
                    Requested
                  </Badge>
                )}
                {original.source === 'CustomerRequested' && (
                  <Badge bg="warning" className="fs-10">
                    Customer requested
                  </Badge>
                )}
                {original.customerName && (
                  <span className="text-body-secondary fs-10">
                    {original.customerName}
                    {original.customerContactNumber
                      ? ` · ${original.customerContactNumber}`
                      : ''}
                  </span>
                )}
                {!original.productId && (
                  <Badge
                    bg="secondary"
                    className="fs-10"
                    title={
                      similarNames
                        ? `Similar in catalog: ${similarNames}`
                        : undefined
                    }
                  >
                    Not in catalog
                  </Badge>
                )}
                {isAdmin && !original.productId && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="py-0"
                    onClick={e => {
                      e.stopPropagation();
                      setCreateFromUnencodedItem(original);
                    }}
                  >
                    Create product
                  </Button>
                )}
              </div>
              <ProductFitmentPreview fitments={original.productFitments} />
            </div>
          );
        }
      },
      {
        id: 'fromPreviousPo',
        header: 'From previous PO',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.wasOrderedBefore ? (
            <span>Yes</span>
          ) : (
            <span className="text-body-tertiary">—</span>
          )
      },
      {
        id: 'quantityOnHand',
        header: 'Current qty',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          if (original.quantityOnHand == null) {
            return <span className="text-body-tertiary">—</span>;
          }
          return (
            <span className="d-inline-flex align-items-center gap-1">
              {original.quantityOnHand}
              {original.isQuantityCorrect === true ? (
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-success"
                  title="Quantity verified"
                />
              ) : (
                <span className="text-body-tertiary fs-10" title="Quantity not verified">
                  unverified
                </span>
              )}
            </span>
          );
        }
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: 'Order qty',
        enableSorting: true,
        cell: ({ row: { original } }) => <span>{original.quantity}</span>
      },
      {
        id: 'comment',
        accessorKey: 'comment',
        header: 'Comment',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.comment ? (
            <span
              className="text-body-secondary fs-9"
              title={original.comment}
              style={{ display: 'inline-block', maxWidth: '16rem', whiteSpace: 'normal' }}
            >
              {original.comment}
            </span>
          ) : (
            <span className="text-body-tertiary">—</span>
          )
      },
      {
        id: 'suppliers',
        header: 'Suppliers',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const item = withEnrichment(original, potentialLookup);
          const primarySupplier = getPrimarySupplier(item);
          const otherSuppliers = getOtherSuppliers(item);
          if (!primarySupplier) {
            return <span className="text-body-tertiary">—</span>;
          }
          return (
            <div className="d-flex align-items-center gap-2 text-nowrap">
              <SupplierName
                compact
                supplierId={primarySupplier.supplierId}
                supplierName={primarySupplier.name}
              />
              {otherSuppliers.length > 0 && (
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="bottom-start"
                  overlay={
                    <Popover
                      id={`resupply-suppliers-${original.resupplyItemId}`}
                    >
                      <Popover.Body className="py-2">
                        <div className="d-flex flex-column gap-1">
                          {otherSuppliers.map(supplier => (
                            <SupplierName
                              key={supplier.supplierId}
                              compact
                              supplierId={supplier.supplierId}
                              supplierName={supplier.name}
                            />
                          ))}
                        </div>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 fs-10 text-nowrap"
                    onClick={event => event.stopPropagation()}
                  >
                    +{otherSuppliers.length} more
                  </Button>
                </OverlayTrigger>
              )}
            </div>
          );
        }
      },
      {
        id: 'movement',
        header: 'Movement',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <ProductMovementBadge
            movementClass={original.movementClass}
            netQuantitySoldLast90Days={original.netQuantitySoldLast90Days}
            distinctSaleDaysLast90Days={original.distinctSaleDaysLast90Days}
            lastSoldOn={original.lastSoldOn}
            firstDeliveredOn={original.firstDeliveredOn}
            tooltipId={`resupply-movement-${original.resupplyItemId}`}
          />
        )
      },
      {
        id: 'createdOn',
        accessorKey: 'createdOn',
        header: 'Date added',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <span>{formatLongIsoDateOrDash(original.createdOn)}</span>
        )
      },
      {
        id: 'lastQuantityOrdered',
        header: 'Last Qty Ordered',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.lastQuantityOrdered != null ? (
            <span>{original.lastQuantityOrdered}</span>
          ) : (
            <span className="text-body-tertiary">—</span>
          )
      },
      {
        id: 'lastDeliveryDate',
        header: 'Last Delivery Date',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.lastDeliveryDate ? (
            <span>{formatLongIsoDate(original.lastDeliveryDate)}</span>
          ) : (
            <span className="text-body-tertiary">—</span>
          )
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={faEdit}
              style={{ cursor: 'pointer' }}
              title="Edit item"
              onClick={e => {
                e.stopPropagation();
                openEditItem(original);
              }}
            />
            <FontAwesomeIcon
              icon={faTrash}
              style={{ cursor: 'pointer', color: '#C63C51' }}
              title="Remove from resupply"
              onClick={e => {
                e.stopPropagation();
                openDeleteConfirm(
                  [original.resupplyItemId],
                  getProductLabel(original)
                );
              }}
            />
          </div>
        )
      }
    ],
    [selectedBranch?.branchDomain, isAdmin, potentialLookup]
  );

  const handleCreatePurchaseOrder = async () => {
    const ids = selectionStore.values();
    if (ids.length === 0) {
      toast.error('Select at least one product', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    const supplierId = supplierIdRef.current;
    if (!supplierId) {
      toast.error('Please select a supplier', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    try {
      const { data } = await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/resupply/purchaseOrders`,
        {
          supplierId,
          resupplyItemIds: ids
        }
      );
      toast.success(
        data.addedToExisting
          ? `Added ${data.itemCount} product${
              data.itemCount === 1 ? '' : 's'
            } to purchase order #${data.purchaseOrderId}`
          : `Purchase order created with ${data.itemCount} item${
              data.itemCount === 1 ? '' : 's'
            }.`,
        { ...toastConfig, autoClose: 3000 }
      );
      modalRef.current.hide();
      selectionStore.clear();
      setIsSubmitted(true);
      navigate(`/purchase-order-details/${data.purchaseOrderId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to create purchase order',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    }
  };

  const openAddItemModal = () => {
    setModalProps({
      title: 'Add to resupply',
      content: (
        <AddResupplyItemForm
          key={Date.now()}
          ref={addItemFormRef}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: async () => {
        const result = await addItemFormRef.current?.submit();
        if (result) {
          modalRef.current.hide();
          refreshList();
        }
      },
      onSubmitCustomDisplay: 'Add to resupply'
    });
    modalRef.current.show();
  };

  const openCreateModal = () => {
    const selectedCount = selectionStore.getSize();
    if (selectedCount === 0) {
      toast.error('Select at least one product', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    supplierIdRef.current = null;
    setModalProps({
      title: 'Create purchase order',
      content: (
        <CreateResupplyPurchaseOrderForm
          selectedCount={selectedCount}
          supplierIdRef={supplierIdRef}
        />
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleCreatePurchaseOrder(),
      onSubmitCustomDisplay: 'Create purchase order'
    });
    modalRef.current.show();
  };

  return (
    <>
      <h4 className="mb-3">Resupply</h4>
      <p className="text-body-secondary fs-9 mb-3">
        Products at or below reorder level, leftover quantity from closed or
        deleted purchase orders, and items requested from chat. Add items
        here, then select products to add to an existing open purchase order
        for that supplier, or create a new one. Click Date added or Movement
        to sort.{' '}
        <Link to="/customer-requests">View customer requests</Link> to see
        which waitlisted items still need to be fulfilled.
      </p>
      <DataTable
        queryKey={[
          'resupply',
          selectedBranch?.branchDomain,
          isSubmitted,
          listVersion
        ]}
        queryFn={fetchData}
        columns={columns}
        onRowsChange={handleRowsChange}
        defaultOrderBy="createdOn"
        defaultOrderByDesc
        filters={filters}
        rightSideActions={
          <>
            <Form.Select
              style={{
                height: '2.4rem',
                width: '16%'
              }}
              value={filters.source ?? ''}
              onChange={e => handleFilterChange('source', e.target.value)}
              aria-label="Filter resupply items by source"
            >
              <option value="">All sources</option>
              {RESUPPLY_SOURCE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {suppliers && (
              <Form.Select
                style={{
                  height: '2.4rem',
                  width: '20%'
                }}
                value={filters.supplierId ?? ''}
                onChange={e => handleFilterChange('supplierId', e.target.value)}
                aria-label="Filter resupply items by supplier"
              >
                <option value="">All suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier.supplierId} value={supplier.supplierId}>
                    {supplier.name}
                  </option>
                ))}
              </Form.Select>
            )}
            <Button
              variant="phoenix-secondary"
              className="w-10 mb-3"
              onClick={openAddItemModal}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add item
            </Button>
            <Link
              to="/purchase-order"
              className="btn btn-phoenix-secondary w-10 mb-3"
            >
              Purchase orders
            </Link>
            <ResupplySelectionActions
              store={selectionStore}
              onDelete={() => openDeleteConfirm(selectionStore.values())}
              onCreate={openCreateModal}
            />
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        onSubmitCustomDisplay={modalProps?.onSubmitCustomDisplay}
      />
      {createFromUnencodedItem != null && (
        <CreateProductFromUnencodedModal
          key={createFromUnencodedItem.resupplyItemId}
          show
          handleClose={() => setCreateFromUnencodedItem(null)}
          query={
            createFromUnencodedItem.productName ||
            `Resupply ${createFromUnencodedItem.resupplyItemId}`
          }
          sourceLabel="Resupply request"
          onBind={bindCreatedProductToResupplyItem}
        />
      )}
    </>
  );
};

export default Resupply;
